/**
 * Веб-арт товаров для витрины сайта: студийные вырезки, сцены, контактные тени.
 *
 * Источник — GET /api/storefront/media (таблица product_web_media, загрузка из
 * админки). Раньше это были JSON-файлы в src/components/storefront, и любая
 * правка арта означала релиз сайта. products.image_url и приложение это не
 * затрагивает: арт живёт отдельно и показывается только на сайте.
 *
 * ПРИВЯЗКА К ФОТО. Каждая запись помнит, к какому фото товара её делали
 * (`source`). Если в админке заменили фото, арт устаревает и витрина сразу
 * показывает новое фото — проверяет это клиент (productPresentation).
 *
 * ОТКАЗОУСТОЙЧИВОСТЬ. Любой сбой (бэк недоступен, ручки ещё нет, кривой ответ)
 * означает «арта нет» — витрина показывает обычные фото из приложения.
 * Нормализатор никогда не бросает и выкидывает битые записи поштучно.
 *
 * Без 'use client': нормализатор и типы нужны и серверу (lib/api/server.ts).
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

/** [x1, y1, x2, y2] — прямоугольник с едой внутри холста, в пикселях. */
export type StorefrontBounds = [number, number, number, number];

export type StorefrontStudio = {
  url: string;
  width: number;
  height: number;
  bounds: StorefrontBounds;
};

/** Линия контакта с «полом»; размеры = ширина и высота bounds студийной вырезки. */
export type StorefrontContact = { width: number; height: number; path: string };

export type StorefrontScene = {
  image: string;
  foreground: string;
  width: number;
  height: number;
  bounds: StorefrontBounds;
  color: string;
  ink: string;
  canvas: string | null;
};

export type StorefrontArt = {
  source: string | null;
  studio: StorefrontStudio | null;
  contact: StorefrontContact | null;
  scene: StorefrontScene | null;
  wide: boolean;
};

export type StorefrontMedia = { products: Record<string, StorefrontArt> };

/** Арт по id товара: `art[product.id]`. Ключи — строки, id товара — число. */
export type StorefrontArtLookup = Partial<Record<string, StorefrontArt>>;

export const EMPTY_STOREFRONT_MEDIA: StorefrontMedia = { products: {} };

/**
 * Миниатюра товара вне витрины (корзина, оформление, «Похожие»): студийная
 * вырезка, если она одобрена к текущему фото товара — то же правило, что в
 * productPresentation, — иначе фото из приложения.
 */
export function productThumbnail(
  product: { id: number; image_url?: string | null },
  art: StorefrontArtLookup
): { src: string; cutout: boolean; studio?: StorefrontStudio } {
  const approved = art[String(product.id)];
  if (approved?.studio && approved.source === product.image_url) {
    return { src: approved.studio.url, cutout: true, studio: approved.studio };
  }
  return { src: product.image_url || '/placeholder.png', cutout: false };
}

export const storefrontMediaKey = ['storefront-media'] as const;

type Json = Record<string, unknown>;

const isObject = (value: unknown): value is Json =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// URL уходит в src и в CSS url("…"): только https или путь от корня сайта,
// без пробелов, кавычек и скобок, которые сломали бы CSS.
const URL_RE = /^(?:https:\/\/|\/(?!\/))[^\s"'()<>\\]+$/;
const COLOR_RE = /^#[0-9a-f]{6}$/i;
const PRODUCT_ID_RE = /^[1-9]\d*$/;

function url(value: unknown): string | null {
  return typeof value === 'string' && value.length <= 500 && URL_RE.test(value) ? value : null;
}

function color(value: unknown): string | null {
  return typeof value === 'string' && COLOR_RE.test(value) ? value : null;
}

function dimension(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 && value <= 65535
    ? value
    : null;
}

/** Размеры холста и bounds внутри него. На (x2 - x1) и (y2 - y1) потом делим. */
function frame(value: Json) {
  const width = dimension(value.width);
  const height = dimension(value.height);
  const bounds = value.bounds;
  if (!width || !height || !Array.isArray(bounds) || bounds.length !== 4) return null;
  if (!bounds.every((n) => typeof n === 'number' && Number.isFinite(n))) return null;
  const [x1, y1, x2, y2] = bounds as number[];
  if (!(x1 >= 0 && y1 >= 0 && x1 < x2 && y1 < y2 && x2 <= width && y2 <= height)) return null;
  return { width, height, bounds: [x1, y1, x2, y2] as StorefrontBounds };
}

function studio(value: unknown): StorefrontStudio | null {
  if (!isObject(value)) return null;
  const image = url(value.url);
  const size = frame(value);
  return image && size ? { url: image, ...size } : null;
}

function contact(value: unknown): StorefrontContact | null {
  if (!isObject(value)) return null;
  const width = value.width;
  const height = value.height;
  const path = value.path;
  if (typeof width !== 'number' || !Number.isFinite(width) || width <= 0) return null;
  if (typeof height !== 'number' || !Number.isFinite(height) || height <= 0) return null;
  return typeof path === 'string' && path.trim() ? { width, height, path } : null;
}

function scene(value: unknown): StorefrontScene | null {
  if (!isObject(value)) return null;
  const image = url(value.image);
  const foreground = url(value.foreground);
  const size = frame(value);
  const main = color(value.color);
  const ink = color(value.ink);
  if (!image || !foreground || !size || !main || !ink) return null;
  // Нет или битый canvas — диалог товара возьмёт основной цвет сцены.
  return { image, foreground, ...size, color: main, ink, canvas: color(value.canvas) };
}

/**
 * Приводит ответ GET /storefront/media к StorefrontMedia. Не бросает.
 * Битая часть записи (студия, сцена, тень) становится null — без студии арт
 * не показывается вовсе, без сцены остаётся студийная вырезка.
 */
export function normalizeStorefrontMedia(data: unknown): StorefrontMedia {
  if (!isObject(data) || !isObject(data.products)) return EMPTY_STOREFRONT_MEDIA;
  const products: Record<string, StorefrontArt> = {};
  for (const [id, value] of Object.entries(data.products)) {
    if (!PRODUCT_ID_RE.test(id) || !isObject(value)) continue;
    products[id] = {
      source: typeof value.source === 'string' ? value.source : null,
      studio: studio(value.studio),
      contact: contact(value.contact),
      scene: scene(value.scene),
      wide: value.wide === true,
    };
  }
  return { products };
}

export async function fetchStorefrontMedia(): Promise<StorefrontMedia> {
  const { data } = await api.get<unknown>('/storefront/media');
  return normalizeStorefrontMedia(data);
}

/**
 * Арт витрины по id товара. Пока данных нет или запрос упал — пустой словарь,
 * то есть обычные фото товаров.
 *
 * initialData — снимок с сервера (SSR главной), чтобы арт был в первом HTML без
 * подмены фото. Пустой снимок не подкладываем: он может означать сбой при
 * рендере, а «свежие» пустые данные на 60 секунд спрятали бы арт.
 *
 * enabled: false — только читать кеш, не запрашивая (закрытая карточка товара
 * монтируется раньше страницы и иначе обгоняла бы снимок с сервера лишним запросом).
 */
export function useStorefrontArt(
  initialData?: StorefrontMedia,
  options?: { enabled?: boolean }
): StorefrontArtLookup {
  const { data } = useQuery({
    queryKey: storefrontMediaKey,
    queryFn: fetchStorefrontMedia,
    initialData: initialData && Object.keys(initialData.products).length ? initialData : undefined,
    staleTime: 60_000,
    ...options,
  });
  return (data ?? EMPTY_STOREFRONT_MEDIA).products;
}
